import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  appsDb,
  commentsDb,
  favoritesDb,
  linksDb,
  notesDb,
  questionsDb,
  tipsDb,
} from '../lib/db/index.js';
import { useAuth } from '../lib/firebase/auth-context.js';
import { VisibilityToggle } from '../components/me/VisibilityToggle.js';
import { DeleteOwnButton } from '../components/me/DeleteOwnButton.js';
import { EditOwnButton } from '../components/me/EditOwnButton.js';
import { Spinner } from '../components/shared/Spinner.js';
import { EmptyState } from '../components/shared/EmptyState.js';
import { Icon, type IconName } from '../components/shared/Icon.js';
import { SourceIcon } from '../components/shared/SourceIcon.js';
import { CommentTypeBadge } from '../components/comments/CommentTypeBadge.js';
import { MyFavorites } from '../components/me/MyFavorites.js';
import { MyDrafts } from '../components/me/MyDrafts.js';
import { MyProjects } from '../components/me/MyProjects.js';
import { projectsDb } from '../lib/db/index.js';
import { timeAgo } from '../lib/utils/time.js';
import { searchByFields } from '../lib/utils/search.js';
import type { Visibility } from '../types/visibility.js';
import type { Link as LinkDoc } from '../types/link.js';
import type { Question } from '../types/question.js';
import type { Note } from '../types/note.js';
import type { Tip } from '../types/tip.js';
import type { AiApp } from '../types/app.js';

type VisFilter = 'all' | 'shared' | 'private';
type TabId =
  | 'all'
  | 'links'
  | 'questions'
  | 'notes'
  | 'tips'
  | 'apps'
  | 'comments'
  | 'projects'
  | 'favorites'
  | 'drafts';

/**
 * 「すべて」タブで横断表示する自分の投稿（links / questions / notes / tips / apps）の merged item。
 * comments / favorites / drafts は性質が異なるので含めない。
 */
type MergedMeItem =
  | { kind: 'link'; data: LinkDoc }
  | { kind: 'question'; data: Question }
  | { kind: 'note'; data: Note }
  | { kind: 'tip'; data: Tip }
  | { kind: 'app'; data: AiApp };

/**
 * 種別ごとの検索対象フィールド。`/search` ページ (SearchPage.tsx) と同じマッピングで、
 * 一貫した検索結果が得られるようにしている。
 */
const SEARCH_FIELDS = {
  link: ['title', 'summary', 'userComment', 'channelName', 'domain', 'sourceType', 'tags'] as (keyof LinkDoc)[],
  question: ['title', 'body', 'tags'] as (keyof Question)[],
  note: ['title', 'purpose', 'result', 'conclusion', 'tags'] as (keyof Note)[],
  tip: ['title', 'body', 'tags'] as (keyof Tip)[],
  app: ['name', 'summary', 'purpose', 'tags'] as (keyof AiApp)[],
};

interface TabDef {
  id: TabId;
  label: string;
  icon: IconName;
  count?: number;
  disabled?: boolean;
}

export function MyPage() {
  const { profile, fbUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<TabId>('all');
  const [visFilter, setVisFilter] = useState<VisFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const uid = fbUser?.uid;

  const linksQ = useQuery({
    queryKey: ['me', 'links', uid],
    queryFn: () => (uid ? linksDb.findByOwner(uid) : Promise.resolve([])),
    enabled: !!uid,
  });
  const questionsQ = useQuery({
    queryKey: ['me', 'questions', uid],
    queryFn: () => (uid ? questionsDb.findByOwner(uid) : Promise.resolve([])),
    enabled: !!uid,
  });
  const notesQ = useQuery({
    queryKey: ['me', 'notes', uid],
    queryFn: () => (uid ? notesDb.findByOwner(uid) : Promise.resolve([])),
    enabled: !!uid,
  });
  const tipsQ = useQuery({
    queryKey: ['me', 'tips', uid],
    queryFn: () => (uid ? tipsDb.findByOwner(uid) : Promise.resolve([])),
    enabled: !!uid,
  });
  const appsQ = useQuery({
    queryKey: ['me', 'apps', uid],
    queryFn: () => (uid ? appsDb.findByOwner(uid) : Promise.resolve([])),
    enabled: !!uid,
  });
  const commentsQ = useQuery({
    queryKey: ['me', 'comments', uid],
    queryFn: () => (uid ? commentsDb.findByOwner(uid) : Promise.resolve([])),
    enabled: !!uid,
  });
  const favsCountQ = useQuery({
    queryKey: ['favorites', uid, 'count'],
    queryFn: () => (uid ? favoritesDb.listByOwner(uid).then((r) => r.length) : Promise.resolve(0)),
    enabled: !!uid,
  });
  const myProjectsCountQ = useQuery({
    queryKey: ['projects', 'mine', uid, 'count'],
    queryFn: () => (uid ? projectsDb.findByOwner(uid).then((r) => r.length) : Promise.resolve(0)),
    enabled: !!uid,
  });

  const counts = useMemo(() => {
    const tally = <T extends { visibility: Visibility }>(arr: T[] | undefined) => {
      const total = arr?.length ?? 0;
      const shared = arr?.filter((x) => x.visibility === 'shared').length ?? 0;
      return { total, shared, privateCount: total - shared };
    };
    return {
      links: tally(linksQ.data),
      questions: tally(questionsQ.data),
      notes: tally(notesQ.data),
      tips: tally(tipsQ.data),
      apps: tally(appsQ.data),
      comments: { total: commentsQ.data?.length ?? 0 },
    };
  }, [linksQ.data, questionsQ.data, notesQ.data, tipsQ.data, appsQ.data, commentsQ.data]);

  /**
   * 4 種の投稿（links / questions / notes / apps）を `createdAt` 降順で 1 つの配列にマージ。
   * 「すべて」タブで横断表示する。kind discriminator で各行の表示を分岐。
   */
  const allItems = useMemo<MergedMeItem[]>(() => {
    const links = (linksQ.data ?? []).map<MergedMeItem>((d) => ({ kind: 'link', data: d }));
    const questions = (questionsQ.data ?? []).map<MergedMeItem>((d) => ({ kind: 'question', data: d }));
    const notes = (notesQ.data ?? []).map<MergedMeItem>((d) => ({ kind: 'note', data: d }));
    const tips = (tipsQ.data ?? []).map<MergedMeItem>((d) => ({ kind: 'tip', data: d }));
    const apps = (appsQ.data ?? []).map<MergedMeItem>((d) => ({ kind: 'app', data: d }));
    return [...links, ...questions, ...notes, ...tips, ...apps].sort(
      (a, b) => b.data.createdAt.getTime() - a.data.createdAt.getTime(),
    );
  }, [linksQ.data, questionsQ.data, notesQ.data, tipsQ.data, appsQ.data]);

  const allTotal =
    counts.links.total + counts.questions.total + counts.notes.total + counts.tips.total + counts.apps.total;

  const unanswered = (questionsQ.data ?? []).filter((q) => q.answerCount === 0).length;
  const answered = (counts.questions.total ?? 0) - unanswered;

  const draftCount = countLocalDrafts();

  const tabs: TabDef[] = [
    { id: 'all', label: 'すべて', icon: 'home', count: allTotal },
    { id: 'links', label: '自分のURL', icon: 'link', count: counts.links.total },
    { id: 'questions', label: '自分の質問', icon: 'qa', count: counts.questions.total },
    { id: 'notes', label: '自分の検証メモ', icon: 'note', count: counts.notes.total },
    { id: 'tips', label: '自分の TIPS', icon: 'bulb', count: counts.tips.total },
    { id: 'apps', label: '自分の作成アプリ', icon: 'app', count: counts.apps.total },
    { id: 'comments', label: 'コメント履歴', icon: 'message', count: counts.comments.total },
    { id: 'projects', label: '自分のトピック', icon: 'project', count: myProjectsCountQ.data ?? 0 },
    { id: 'favorites', label: 'お気に入り', icon: 'star', count: favsCountQ.data ?? 0 },
    { id: 'drafts', label: '下書き', icon: 'note', count: draftCount },
  ];

  const visMatch = (v: Visibility) =>
    visFilter === 'all' ? true : visFilter === v;

  const matchesQuery = <T,>(item: T, fields: (keyof T)[]) =>
    !searchQuery.trim() || searchByFields([item], searchQuery, fields).length > 0;

  const compose = (kind: 'link' | 'note' | 'tip' | 'app') => {
    const next = new URLSearchParams(params);
    next.set('compose', kind);
    setParams(next);
  };

  if (!uid || !profile) return null;

  const initials = profile.name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s.charAt(0))
    .join('')
    .slice(0, 2);

  return (
    <div className="page">
      <div className="me-header">
        <div
          className="me-avatar"
          style={{
            background: `${profile.color}22`,
            color: profile.color,
            borderColor: `${profile.color}44`,
          }}
        >
          {initials}
        </div>
        <div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--ink-3)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
            }}
          >
            MY PAGE
          </div>
          <div className="me-name">
            {profile.name}{' '}
            <span style={{ color: 'var(--ink-3)', fontSize: 14, fontWeight: 400 }}>さん</span>
          </div>
          <div className="me-role">{profile.role} · @{profile.handle}</div>
          {fbUser?.email && <div className="me-email">{fbUser.email}</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button type="button" className="btn sm" onClick={() => compose('link')}>
            <Icon name="plus" size={12} />
            URLを登録
          </button>
          <button type="button" className="btn sm" onClick={() => compose('note')}>
            <Icon name="plus" size={12} />
            検証メモを書く
          </button>
          <button type="button" className="btn sm" onClick={() => compose('app')}>
            <Icon name="plus" size={12} />
            作成アプリを登録
          </button>
        </div>
      </div>

      <div className="section-title">自分の登録状況</div>
      <div className="me-stats">
        <Stat label="登録URL" value={counts.links.total} unit="件" splits={[`共有 ${counts.links.shared}`, `非公開 ${counts.links.privateCount}`]} />
        <Stat label="質問" value={counts.questions.total} unit="件" splits={[`未回答 ${unanswered}`, `回答済 ${answered}`]} />
        <Stat label="検証メモ" value={counts.notes.total} unit="件" splits={[`共有 ${counts.notes.shared}`, `非公開 ${counts.notes.privateCount}`]} />
        <Stat label="作成アプリ" value={counts.apps.total} unit="件" splits={[`共有 ${counts.apps.shared}`, `非公開 ${counts.apps.privateCount}`]} />
        <Stat label="コメント" value={counts.comments.total} unit="件" />
      </div>

      <div className="me-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`me-tab ${t.id === tab ? 'active' : ''}`}
            onClick={() => !t.disabled && setTab(t.id)}
            disabled={t.disabled}
            style={t.disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
          >
            <Icon name={t.icon} size={13} />
            {t.label}
            {t.count !== undefined && <span className="me-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {(tab === 'all' || tab === 'links' || tab === 'questions' || tab === 'notes' || tab === 'tips' || tab === 'apps') && (
        <div className="me-filter">
          <div className="vis-switch">
            <button
              type="button"
              className={visFilter === 'all' ? 'active' : ''}
              onClick={() => setVisFilter('all')}
            >
              すべて
            </button>
            <button
              type="button"
              className={visFilter === 'shared' ? 'active' : ''}
              onClick={() => setVisFilter('shared')}
            >
              共有中
            </button>
            <button
              type="button"
              className={visFilter === 'private' ? 'active' : ''}
              onClick={() => setVisFilter('private')}
            >
              非公開
            </button>
          </div>
          <input
            type="search"
            className="list-search"
            placeholder="自分の投稿を検索（スペース区切りで AND）"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="マイページ内検索"
          />
        </div>
      )}

      {tab === 'all' && (
        <AllOwnedList
          items={allItems}
          isPending={
            linksQ.isPending ||
            questionsQ.isPending ||
            notesQ.isPending ||
            tipsQ.isPending ||
            appsQ.isPending
          }
          filter={(it) => {
            if (!visMatch(it.data.visibility)) return false;
            switch (it.kind) {
              case 'link': return matchesQuery(it.data, SEARCH_FIELDS.link);
              case 'question': return matchesQuery(it.data, SEARCH_FIELDS.question);
              case 'note': return matchesQuery(it.data, SEARCH_FIELDS.note);
              case 'tip': return matchesQuery(it.data, SEARCH_FIELDS.tip);
              case 'app': return matchesQuery(it.data, SEARCH_FIELDS.app);
            }
          }}
          uid={uid}
        />
      )}

      {tab === 'links' && (
        <OwnedList
          q={linksQ}
          filter={(item) => visMatch(item.visibility) && matchesQuery(item, SEARCH_FIELDS.link)}
          renderRow={(l) => (
            <LinkMeRow
              link={l}
              uid={uid}
            />
          )}
          rowKey="id"
        />
      )}

      {tab === 'questions' && (
        <OwnedList
          q={questionsQ}
          filter={(item) => visMatch(item.visibility) && matchesQuery(item, SEARCH_FIELDS.question)}
          renderRow={(q) => <QuestionMeRow q={q} uid={uid} />}
          rowKey="id"
        />
      )}

      {tab === 'notes' && (
        <OwnedList
          q={notesQ}
          filter={(item) => visMatch(item.visibility) && matchesQuery(item, SEARCH_FIELDS.note)}
          renderRow={(n) => <NoteMeRow note={n} uid={uid} />}
          rowKey="id"
        />
      )}

      {tab === 'tips' && (
        <OwnedList
          q={tipsQ}
          filter={(item) => visMatch(item.visibility) && matchesQuery(item, SEARCH_FIELDS.tip)}
          renderRow={(t) => <TipMeRow tip={t} uid={uid} />}
          rowKey="id"
        />
      )}

      {tab === 'apps' && (
        <OwnedList
          q={appsQ}
          filter={(item) => visMatch(item.visibility) && matchesQuery(item, SEARCH_FIELDS.app)}
          renderRow={(a) => <AppMeRow app={a} uid={uid} />}
          rowKey="id"
        />
      )}

      {tab === 'comments' && <CommentHistory items={commentsQ.data ?? []} isPending={commentsQ.isPending} />}

      {tab === 'projects' && <MyProjects uid={uid} />}

      {tab === 'favorites' && <MyFavorites uid={uid} />}

      {tab === 'drafts' && <MyDrafts />}
    </div>
  );
}

function countLocalDrafts(): number {
  let n = 0;
  for (const kind of ['link', 'question', 'note', 'tip', 'app']) {
    try {
      const raw = localStorage.getItem(`aidev:draft:${kind}`);
      if (raw && raw.length > 2) n += 1; // 空 JSON 「{}」は数えない
    } catch {
      /* ignore */
    }
  }
  return n;
}

function Stat({
  label,
  value,
  unit,
  splits,
}: {
  label: string;
  value: number;
  unit?: string;
  splits?: string[];
}) {
  return (
    <div className="me-stat">
      <div className="me-stat-label">{label}</div>
      <div className="me-stat-value">
        {value}
        {unit && <span style={{ fontSize: 13, color: 'var(--ink-3)', fontWeight: 500 }}>{unit}</span>}
      </div>
      {splits && splits.length > 0 && (
        <div className="me-stat-split">
          {splits.map((s, i) => (
            <span key={i} className={i === 0 && splits[0]!.startsWith('共有') ? 'sp-shared' : 'sp-private'}>
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function OwnedList<T extends { id: string }>(props: {
  q: { isPending: boolean; data: T[] | undefined };
  filter: (item: T) => boolean;
  rowKey: keyof T;
  renderRow: (item: T) => JSX.Element;
}) {
  if (props.q.isPending) return <Spinner />;
  const items = (props.q.data ?? []).filter(props.filter);
  if (items.length === 0) {
    return <EmptyState title="該当する登録はありません" />;
  }
  return (
    <div style={{ borderTop: '1px solid var(--line)' }}>
      {items.map((item) => (
        <div key={String(item[props.rowKey])}>{props.renderRow(item)}</div>
      ))}
    </div>
  );
}

/**
 * 「すべて」タブ用の横断リスト。kind discriminator で各 MeRow を dispatch。
 * key は `${kind}-${id}` で衝突回避（異種 ID が偶然衝突した場合の安全策）。
 */
function AllOwnedList(props: {
  items: MergedMeItem[];
  isPending: boolean;
  filter: (item: MergedMeItem) => boolean;
  uid: string;
}) {
  if (props.isPending) return <Spinner />;
  const items = props.items.filter(props.filter);
  if (items.length === 0) {
    return <EmptyState title="登録はまだありません" />;
  }
  return (
    <div style={{ borderTop: '1px solid var(--line)' }}>
      {items.map((it) => {
        const key = `${it.kind}-${it.data.id}`;
        switch (it.kind) {
          case 'link':
            return <div key={key}><LinkMeRow link={it.data} uid={props.uid} /></div>;
          case 'question':
            return <div key={key}><QuestionMeRow q={it.data} uid={props.uid} /></div>;
          case 'note':
            return <div key={key}><NoteMeRow note={it.data} uid={props.uid} /></div>;
          case 'tip':
            return <div key={key}><TipMeRow tip={it.data} uid={props.uid} /></div>;
          case 'app':
            return <div key={key}><AppMeRow app={it.data} uid={props.uid} /></div>;
        }
      })}
    </div>
  );
}

function VisBadge({ v }: { v: Visibility }) {
  return (
    <span className="vis-badge" data-vis={v}>
      {v === 'shared' ? '共有中' : '非公開'}
    </span>
  );
}

function LinkMeRow({ link, uid }: { link: LinkDoc; uid: string }) {
  const nav = useNavigate();
  // 概要は summary → userComment → ソース情報 の順で fallback。
  // X 投稿で summary 空でも「@user · X」のような情報が必ず 1 行出るようにする。
  const desc =
    link.summary ||
    link.userComment ||
    `${link.channelName ?? link.domain} · ${link.sourceType}`;
  return (
    <div className="me-row">
      <div className="me-row-icon">
        <SourceIcon sourceType={link.sourceType} domain={link.domain} size={18} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="me-row-title" onClick={() => nav(`/links/${link.id}`)}>{link.title}</div>
        <div className="me-row-summary">{desc}</div>
        <div className="me-row-meta">
          <VisBadge v={link.visibility} />
          {link.tags.slice(0, 3).map((t) => (
            <span key={t} className="tag"><span className="tag-dot" />{t}</span>
          ))}
          <span>· {timeAgo(link.createdAt)}</span>
        </div>
      </div>
      <div className="me-actions">
        <Link to={`/links/${link.id}`} className="btn xs">詳細</Link>
        <VisibilityToggle
          current={link.visibility}
          invalidateKeys={[
            ['me', 'links', uid],
            ['links', 'shared'],
            ['home', 'recentLinks'],
          ]}
          setVisibility={(v) => linksDb.updateVisibility(link.id, v)}
        />
        <EditOwnButton kind="link" id={link.id} />
        <DeleteOwnButton
          kind="URL"
          title={link.title}
          onDelete={() => linksDb.remove(link.id)}
          invalidateKeys={[
            ['me', 'links', uid],
            ['links', 'shared'],
            ['home', 'recentLinks'],
            ['sidebar', 'counts'],
          ]}
        />
      </div>
    </div>
  );
}

function QuestionMeRow({ q, uid }: { q: Question; uid: string }) {
  const nav = useNavigate();
  return (
    <div className="me-row">
      <div className="me-row-icon">Q&A</div>
      <div style={{ minWidth: 0 }}>
        <div className="me-row-title" onClick={() => nav(`/qa/${q.id}`)}>{q.title}</div>
        <div className="me-row-meta">
          <VisBadge v={q.visibility} />
          <span className="badge" data-status={q.status}><span className="badge-dot" />{q.status}</span>
          <span>· 回答 {q.answerCount}</span>
          <span>· {timeAgo(q.createdAt)}</span>
        </div>
      </div>
      <div className="me-actions">
        <Link to={`/qa/${q.id}`} className="btn xs">詳細</Link>
        <VisibilityToggle
          current={q.visibility}
          invalidateKeys={[
            ['me', 'questions', uid],
            ['questions', 'shared'],
            ['home', 'unansweredQuestions'],
          ]}
          setVisibility={(v) => questionsDb.updateVisibility(q.id, v)}
        />
        <EditOwnButton kind="question" id={q.id} />
        <DeleteOwnButton
          kind="質問"
          title={q.title}
          onDelete={() => questionsDb.remove(q.id)}
          invalidateKeys={[
            ['me', 'questions', uid],
            ['questions', 'shared'],
            ['home', 'unansweredQuestions'],
            ['sidebar', 'counts'],
          ]}
        />
      </div>
    </div>
  );
}

function NoteMeRow({ note, uid }: { note: Note; uid: string }) {
  const nav = useNavigate();
  return (
    <div className="me-row">
      <div className="me-row-icon">NOTE</div>
      <div style={{ minWidth: 0 }}>
        <div className="me-row-title" onClick={() => nav(`/notes/${note.id}`)}>{note.title}</div>
        <div className="me-row-meta">
          <VisBadge v={note.visibility} />
          {note.tags.slice(0, 3).map((t) => (
            <span key={t} className="tag"><span className="tag-dot" />{t}</span>
          ))}
          <span>· {timeAgo(note.createdAt)}</span>
        </div>
      </div>
      <div className="me-actions">
        <Link to={`/notes/${note.id}`} className="btn xs">詳細</Link>
        <VisibilityToggle
          current={note.visibility}
          invalidateKeys={[
            ['me', 'notes', uid],
            ['notes', 'shared'],
            ['home', 'recentNotes'],
          ]}
          setVisibility={(v) => notesDb.updateVisibility(note.id, v)}
        />
        <EditOwnButton kind="note" id={note.id} />
        <DeleteOwnButton
          kind="検証メモ"
          title={note.title}
          onDelete={() => notesDb.remove(note.id)}
          invalidateKeys={[
            ['me', 'notes', uid],
            ['notes', 'shared'],
            ['home', 'recentNotes'],
            ['sidebar', 'counts'],
          ]}
        />
      </div>
    </div>
  );
}

function TipMeRow({ tip, uid }: { tip: Tip; uid: string }) {
  const nav = useNavigate();
  return (
    <div className="me-row">
      <div className="me-row-icon">TIP</div>
      <div style={{ minWidth: 0 }}>
        <div className="me-row-title" onClick={() => nav(`/tips/${tip.id}`)}>{tip.title}</div>
        <div className="me-row-meta">
          <VisBadge v={tip.visibility} />
          {tip.tags.slice(0, 3).map((t) => (
            <span key={t} className="tag"><span className="tag-dot" />{t}</span>
          ))}
          <span>· {timeAgo(tip.createdAt)}</span>
        </div>
      </div>
      <div className="me-actions">
        <Link to={`/tips/${tip.id}`} className="btn xs">詳細</Link>
        <VisibilityToggle
          current={tip.visibility}
          invalidateKeys={[
            ['me', 'tips', uid],
            ['tips', 'shared'],
          ]}
          setVisibility={(v) => tipsDb.updateVisibility(tip.id, v)}
        />
        <EditOwnButton kind="tip" id={tip.id} />
        <DeleteOwnButton
          kind="TIPS"
          title={tip.title}
          onDelete={() => tipsDb.remove(tip.id)}
          invalidateKeys={[
            ['me', 'tips', uid],
            ['tips', 'shared'],
            ['sidebar', 'counts'],
          ]}
        />
      </div>
    </div>
  );
}

function AppMeRow({ app, uid }: { app: AiApp; uid: string }) {
  const nav = useNavigate();
  return (
    <div className="me-row">
      <div className="me-row-icon">APP</div>
      <div style={{ minWidth: 0 }}>
        <div className="me-row-title" onClick={() => nav(`/apps/${app.id}`)}>{app.name}</div>
        <div className="me-row-meta">
          <VisBadge v={app.visibility} />
          <span className="badge" data-status={app.status}><span className="badge-dot" />{app.status}</span>
          <span>· {timeAgo(app.createdAt)}</span>
        </div>
      </div>
      <div className="me-actions">
        <Link to={`/apps/${app.id}`} className="btn xs">詳細</Link>
        <VisibilityToggle
          current={app.visibility}
          invalidateKeys={[
            ['me', 'apps', uid],
            ['apps', 'shared'],
            ['home', 'recentApps'],
          ]}
          setVisibility={(v) => appsDb.updateVisibility(app.id, v)}
        />
        <EditOwnButton kind="app" id={app.id} />
        <DeleteOwnButton
          kind="作成アプリ"
          title={app.name}
          onDelete={() => appsDb.remove(app.id)}
          invalidateKeys={[
            ['me', 'apps', uid],
            ['apps', 'shared'],
            ['home', 'recentApps'],
            ['sidebar', 'counts'],
          ]}
        />
      </div>
    </div>
  );
}

function CommentHistory({
  items,
  isPending,
}: {
  items: import('../types/comment.js').Comment[];
  isPending: boolean;
}) {
  if (isPending) return <Spinner />;
  if (items.length === 0) return <EmptyState title="まだコメントはありません" />;
  return (
    <ul className="comment-list">
      {items.map((c) => (
        <li key={c.id} className="comment-item">
          <div className="comment-head">
            <CommentTypeBadge value={c.type} />
            <span className="mono">{c.targetType}</span>
            <Link to={`/${c.targetType}s/${c.targetId}`} className="mono comment-target-link">
              → 対象を見る
            </Link>
            <time className="comment-time mono">
              {c.createdAt.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
            </time>
          </div>
          <div className="comment-body prose">{c.body}</div>
        </li>
      ))}
    </ul>
  );
}
